import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import type { HelpDeskReply, HelpDeskTicket } from "../../Models/helpDesk";
import { useAddTicketReply, useHelpDeskAccess } from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import {
  formatMessageDateTime,
  formatMessageTime,
} from "../../Helpers/dateHelpers";
import AttachmentList from "./AttachmentList";
import AttachmentInput, { useAttachmentFiles } from "./AttachmentInput";

type Props = {
  ticket: HelpDeskTicket;
  onSent?: () => void;
};

/**
 * Conversation column of a ticket: own messages on the right, everyone else's
 * on the left, and replies from anyone other than the submitter marked as
 * support. Parents pass `key={ticket.id}` so the draft resets per ticket.
 */
const TicketConversation: React.FC<Props> = ({ ticket, onSent }) => {
  const { t } = useTranslation("helpDesk");
  const { data: access } = useHelpDeskAccess();
  const replyMutation = useAddTicketReply();
  const attachments = useAttachmentFiles();
  const [reply, setReply] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const replies = ticket.replies ?? [];
  const canSend = Boolean(reply.trim() || attachments.files.length);

  useEffect(() => {
    if (replies.length) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [ticket.id, replies.length]);

  const sendReply = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSend || replyMutation.isPending) return;

    replyMutation.mutate(
      {
        id: ticket.id,
        reply: { message: reply.trim(), files: attachments.files },
      },
      {
        onSuccess: () => {
          setReply("");
          attachments.clear();
          onSent?.();
        },
      },
    );
  };

  const pasteScreenshot = (e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData.files);
    if (pasted.length) {
      e.preventDefault();
      attachments.add(pasted);
    }
  };

  const renderMessage = (item: HelpDeskReply) => {
    const isMine = item.author?.id === access?.user_id;
    const isSupport = Boolean(
      item.author && item.author.id !== ticket.created_by?.id,
    );
    const name = item.author
      ? `${item.author.name} ${item.author.surname}`.trim()
      : t("tickets.unknownUser");

    return (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          justifyContent: isMine ? "flex-end" : "flex-start",
          width: "100%",
        }}
      >
        <Stack
          direction={isMine ? "row-reverse" : "row"}
          spacing={1}
          alignItems="flex-end"
          sx={{ maxWidth: "85%", minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: isSupport ? color.accent : color.secondary,
              color: color.white,
              fontSize: 11,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>

          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: isMine ? "flex-end" : "flex-start",
            }}
          >
            <Stack
              direction={isMine ? "row-reverse" : "row"}
              spacing={0.75}
              alignItems="center"
              sx={{ mb: 0.5, mx: 0.5 }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: `${color.gray500} !important`,
                }}
              >
                {isMine ? t("conversation.you") : name}
              </Typography>
              {isSupport && (
                <Chip
                  label={t("conversation.supportBadge")}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 800,
                    bgcolor: `${color.orange50} !important`,
                    color: `${color.accent_dark} !important`,
                  }}
                />
              )}
            </Stack>

            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: isMine ? color.secondary : color.white,
                border: isMine ? "none" : `1px solid ${color.gray200}`,
                borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                boxShadow: isMine
                  ? "0 2px 8px rgba(0,0,0,0.08)"
                  : "0 2px 6px rgba(0,0,0,0.03)",
                maxWidth: "100%",
              }}
            >
              {item.message && (
                <Typography
                  sx={{
                    color: isMine
                      ? `${color.white} !important`
                      : `${color.primary_dark} !important`,
                    fontSize: 13,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {item.message}
                </Typography>
              )}
              <AttachmentList attachments={item.attachments ?? []} />
            </Box>

            <Tooltip title={formatMessageDateTime(item.created_at)} arrow placement="top">
              <Typography
                component="span"
                sx={{
                  mt: 0.5,
                  mx: 0.5,
                  fontSize: 10,
                  lineHeight: 1.4,
                  color: `${color.gray500} !important`,
                  cursor: "default",
                }}
              >
                {formatMessageTime(item.created_at)}
              </Typography>
            </Tooltip>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: color.white,
          borderBottom: `1px solid ${color.gray200}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              bgcolor: color.blue50,
              display: "grid",
              placeItems: "center",
            }}
          >
            <ChatBubbleOutlineIcon sx={{ color: color.secondary, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: `${color.primary_dark} !important`,
                fontSize: 15,
                fontWeight: 800,
              }}
            >
              {t("conversation.title")}
            </Typography>
            <Typography sx={{ color: `${color.gray500} !important`, fontSize: 11 }}>
              {t("ticketModal.messages", { count: replies.length })}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: { xs: 2, md: 3 },
          py: 2.5,
        }}
      >
        {replies.length ? (
          <Stack spacing={2.5}>
            {replies.map(renderMessage)}
            <div ref={chatEndRef} />
          </Stack>
        ) : (
          <Box
            sx={{
              height: "100%",
              minHeight: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                bgcolor: color.blue50,
                display: "grid",
                placeItems: "center",
                mb: 2,
              }}
            >
              <ChatBubbleOutlineIcon sx={{ color: color.secondary, fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                color: `${color.gray500} !important`,
                fontSize: 13,
                lineHeight: 1.7,
                maxWidth: 280,
              }}
            >
              {t("conversation.empty")}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={sendReply}
        sx={{
          p: { xs: 2, md: 2.5 },
          bgcolor: color.white,
          borderTop: `1px solid ${color.gray200}`,
          flexShrink: 0,
        }}
      >
        {replyMutation.isError && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: "10px" }}>
            {t("conversation.sendError")}
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder={t("conversation.replyPlaceholder")}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onPaste={pasteScreenshot}
          disabled={replyMutation.isPending}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: `${color.gray50} !important`,
              borderRadius: "12px",
              color: `${color.primary_dark} !important`,
              fontSize: 13,
            },
            "& textarea": {
              color: `${color.primary_dark} !important`,
              WebkitTextFillColor: `${color.primary_dark} !important`,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${color.gray200} !important`,
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: `${color.secondary} !important`,
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
            mt: 1.5,
          }}
        >
          <AttachmentInput
            files={attachments.files}
            error={attachments.error}
            onAdd={attachments.add}
            onRemove={attachments.remove}
            disabled={replyMutation.isPending}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={
              replyMutation.isPending ? (
                <CircularProgress size={15} sx={{ color: color.white }} />
              ) : (
                <SendRoundedIcon />
              )
            }
            disabled={replyMutation.isPending || !canSend}
            sx={{
              flexShrink: 0,
              bgcolor: `${color.accent} !important`,
              color: `${color.white} !important`,
              WebkitTextFillColor: `${color.white} !important`,
              borderRadius: "9px",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 800,
              px: 2.5,
              py: 1.1,
              boxShadow: "none",
              "&:hover": { bgcolor: `${color.accent_dark} !important` },
              "&.Mui-disabled": {
                bgcolor: `${color.gray200} !important`,
                color: `${color.gray500} !important`,
              },
            }}
          >
            {replyMutation.isPending ? t("conversation.sending") : t("conversation.send")}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default TicketConversation;
